// Casting cost predicates. Pure. Takes a catalog card + a resources
// snapshot and returns a structured affordability answer.
//
// ── Codex references ──
//   Cost. "The mana cost is the printed cost of the spell."
//   Threshold. Minimum elemental affinity needed to cast a spell.
//     Thresholds are GATES, not consumed: meeting them costs nothing.
//   Mana. Consumed on cast; "you may not generate mana on your
//     opponent's turn." Resource-payment timing is a turn-bound rule.
//   Casting Sites. Site placement is a separate per-turn action,
//     mana-free, with its own (not-yet-enforced) adjacency rules.
//     Sites pass `isFreeToPlay` and short-circuit the affordability
//     check.
//
// ── Shapes ──
//
//   fullCard. Catalog entry. We read guardian.cost and
//                      guardian.thresholds (air/earth/fire/water).
//   resources. { mana, air, earth, fire, water } numbers.
//                      Caller maps from its own state (e.g. tracker
//                      uses 'wind' for air; helper renames at the
//                      boundary, not here).
//
//   getCardCost(fullCard)
//     → { mana: number, thresholds: { air, earth, fire, water } }
//   isFreeToPlay(fullCard)
//     → true for Sites (site-placement is the orthogonal mechanic).
//   getAffordability(fullCard, resources)
//     → { ok: boolean, refusals: Refusal[] }
//     Where Refusal is either:
//       { kind: 'mana', need, have }
//       { kind: 'threshold', element: 'air'|'earth'|'fire'|'water', need, have }
//       { kind: 'unknown_card' }   // catalog miss
//   formatRefusal(refusal)        → short phrase, e.g. "3 mana (have 1)"
//   formatRefusalsSentence(...)   → joined with " and ", no trailing dot

const ELEMENTS = ['air', 'earth', 'fire', 'water'];
const ELEMENT_SYMBOL = { air: '(A)', earth: '(E)', fire: '(F)', water: '(W)' };

export function getCardCost(fullCard) {
  // Normalized catalog cards (see utils/sorcery/normalizeCards.js)
  // flatten guardian.* onto the top level: `cost` becomes a string
  // (statToString), thresholds become `<element>Threshold` keys.
  // Raw/un-normalized cards expose `guardian.cost` and
  // `guardian.thresholds.<element>`. Both shapes are accepted so
  // GameBoard's normalized catalog AND raw test fixtures work
  // without a separate adapter.
  const guard = fullCard?.guardian || {};
  const thr = guard.thresholds || {};
  const costRaw = fullCard?.cost ?? guard.cost;
  const costNum = parseInt(costRaw, 10);
  return {
    mana: Number.isFinite(costNum) ? costNum : 0,
    thresholds: {
      air:   (fullCard?.airThreshold   ?? thr.air)   || 0,
      earth: (fullCard?.earthThreshold ?? thr.earth) || 0,
      fire:  (fullCard?.fireThreshold  ?? thr.fire)  || 0,
      water: (fullCard?.waterThreshold ?? thr.water) || 0,
    },
  };
}

export function isFreeToPlay(fullCard) {
  // Same dual-shape acceptance: normalized cards expose `type` at top
  // level; raw cards use guardian.type.
  const t = fullCard?.type ?? fullCard?.guardian?.type;
  return t === 'Site';
}

export function getAffordability(fullCard, resources, opts = {}) {
  if (!fullCard) return { ok: false, refusals: [{ kind: 'unknown_card' }] };
  if (isFreeToPlay(fullCard)) return { ok: true, refusals: [] };
  const cost = getCardCost(fullCard);
  // v1.89. Flat cost adjustment from active modifiers (Tax Collector,
  // Witch's Curse, etc). Floors at 0 so a discount can't take a 2-cost
  // spell below 0.
  const adjustedMana = Math.max(0, cost.mana + (opts.costAdjustment || 0));
  const refusals = [];
  const haveMana = (resources?.mana || 0);
  if (haveMana < adjustedMana) {
    refusals.push({ kind: 'mana', need: adjustedMana, have: haveMana });
  }
  for (const element of ELEMENTS) {
    const need = cost.thresholds[element];
    if (need <= 0) continue;
    const have = (resources?.[element] || 0);
    if (have < need) {
      refusals.push({ kind: 'threshold', element, need, have });
    }
  }
  return { ok: refusals.length === 0, refusals };
}

export function formatRefusal(refusal) {
  if (refusal.kind === 'mana') {
    return `${refusal.need} mana (have ${refusal.have})`;
  }
  if (refusal.kind === 'threshold') {
    const sym = ELEMENT_SYMBOL[refusal.element] || '(?)';
    return `${refusal.need} ${sym} (have ${refusal.have})`;
  }
  if (refusal.kind === 'unknown_card') return 'a known card';
  return 'cost';
}

export function formatRefusalsSentence(refusals) {
  if (!refusals?.length) return '';
  return refusals.map(formatRefusal).join(' and ');
}
